/**
 * Picks the REAL worked example for the compare helper from the live
 * preview: the first item (display order) on the rule's layer whose compare
 * window carries the compared metric — preferring one that also has a value
 * for the condition's own metric, so the example can say whether that item
 * matches right now. Pure; returns null when the data simply isn't loaded,
 * and the copy falls back to the synthetic 100-sample.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  resolveCompareWindow,
  ruleCompareWindow,
  type FlowLayer,
  type FlowOptimizerItemPreview,
  type RuleCompareTo,
} from '@trafficpulse/shared';

import type { CompareSample } from '../compare-example';

function read(
  item: FlowOptimizerItemPreview,
  cubeKey: string,
  metric: string,
): number | undefined {
  const v = item.metricsByDateRange?.[cubeKey]?.[metric];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

export interface PickCompareSampleOptions {
  items: ReadonlyArray<FlowOptimizerItemPreview> | undefined;
  layer: FlowLayer;
  /** The condition's own (left-hand) metric. */
  metric: string;
  compareTo: RuleCompareTo;
  /** Cube key the rule's own conditions read — the inherit target. */
  windowKey: string | null;
}

export function pickCompareSample(
  opts: PickCompareSampleOptions,
): CompareSample | null {
  const win = resolveCompareWindow(ruleCompareWindow(opts.compareTo));
  if (win.kind === 'invalid') return null;
  const rhsKey = win.kind === 'key' ? win.cubeKey : opts.windowKey;
  if (!rhsKey) return null;
  let fallback: CompareSample | null = null;
  for (const item of opts.items ?? []) {
    if (item.layer !== opts.layer) continue;
    const rhsValue = read(item, rhsKey, opts.compareTo.metric);
    if (rhsValue === undefined) continue;
    const lhsValue = opts.windowKey
      ? read(item, opts.windowKey, opts.metric)
      : undefined;
    const sample: CompareSample = { itemName: item.itemName, rhsValue, lhsValue };
    if (lhsValue !== undefined) return sample;
    fallback = fallback ?? sample;
  }
  return fallback;
}
